import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../config/firebase';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import {
    addToQueue,
    getQueue,
    getQueueCount,
    removeFromQueue,
    updateQueueItem
} from '../utils/syncQueue';
import logger from '../utils/logger';
import { sanitizeForFirestore } from '../utils/sanitize';

const SyncQueueContext = createContext(null);

export const useSyncQueue = () => {
    const context = useContext(SyncQueueContext);
    if (!context) {
        throw new Error('useSyncQueue must be used within SyncQueueProvider');
    }
    return context;
};

export const SyncQueueProvider = ({ children }) => {
    const [pendingCount, setPendingCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [syncError, setSyncError] = useState(null);

    const user = auth.currentUser;

    // Update pending count
    const updatePendingCount = useCallback(async () => {
        if (!user) return;
        const count = await getQueueCount(user.uid);
        setPendingCount(count);
    }, [user]);



    // Execute a single operation
    const executeOperation = async (operation) => {
        const { type, collection, docId, data } = operation;
        const docRef = doc(db, collection, docId);

        switch (type) {
            case 'CREATE':
            case 'UPDATE':
                // Sanitize data to ensure no undefined values are passed to Firestore
                const cleanData = data ? sanitizeForFirestore(data) : data;
                await setDoc(docRef, cleanData, { merge: type === 'UPDATE' });
                break;
            case 'DELETE':
                await deleteDoc(docRef);
                break;
            default:
                throw new Error(`Unknown operation type: ${type}`);
        }
    };

    // Sync all pending operations
    const syncQueue = useCallback(async () => {
        if (!user || !navigator.onLine || isSyncing) return;

        setIsSyncing(true);
        setSyncError(null);

        try {
            // Loop until queue is drained (handle items added while syncing)
            let hasPending = true;

            while (hasPending) {
                const queue = await getQueue(user.uid);
                const pendingItems = queue.filter(item => item.status === 'pending');

                if (pendingItems.length === 0) {
                    hasPending = false;
                    break;
                }

                for (const item of pendingItems) {
                    try {
                        // Mark as syncing
                        await updateQueueItem(item.id, { status: 'syncing' });

                        // Execute operation
                        await executeOperation(item);

                        // Remove from queue on success
                        await removeFromQueue(item.id);
                        logger.dataUpdate(item.collection, item.docId, user.uid);

                    } catch (error) {
                        console.error('Sync error for item:', item.id, error);

                        // Check if it's a permission error
                        const isPermissionError = error.code === 'permission-denied' ||
                            error.message?.includes('Missing or insufficient permissions');

                        if (isPermissionError) {
                            // Permission errors won't be fixed by retrying, so remove immediately
                            console.warn('Permission error detected, removing item from queue:', item.id);
                            await removeFromQueue(item.id);
                            continue;
                        }

                        // Update retry count
                        const retries = (item.retries || 0) + 1;

                        if (retries >= 3) {
                            // Mark as error after 3 retries
                            await updateQueueItem(item.id, {
                                status: 'error',
                                retries,
                                lastError: error.message
                            });
                        } else {
                            // Reset to pending for retry
                            await updateQueueItem(item.id, {
                                status: 'pending',
                                retries
                            });
                        }
                    }
                }
            }

            setLastSyncTime(new Date());
            await updatePendingCount();



        } catch (error) {
            console.error('Sync queue error:', error);
            setSyncError(error.message);
        } finally {
            setIsSyncing(false);
        }
    }, [user, isSyncing, updatePendingCount]);

    // Queue an operation (Offline-First strategy for performance)
    const queueOperation = useCallback(async (operation) => {
        if (!user) {
            throw new Error('User not authenticated');
        }

        const operationWithUser = {
            ...operation,
            userId: user.uid
        };

        // 1. Coalescing Logic (Update Merging)
        // Check if there's already a PENDING operation for this document
        if (operation.type === 'UPDATE' || operation.type === 'CREATE') {
            try {
                const queue = await getQueue(user.uid);
                // Find existing pending item for same doc
                const pendingItem = queue.find(item =>
                    item.status === 'pending' &&
                    item.collection === operation.collection &&
                    item.docId === operation.docId &&
                    (item.type === 'UPDATE' || item.type === 'CREATE')
                );

                if (pendingItem) {
                    // MERGE updates into existing item instead of queuing new one
                    const mergedData = { ...pendingItem.data, ...operation.data };
                    await updateQueueItem(pendingItem.id, { data: mergedData });

                    // Trigger sync just in case
                    if (navigator.onLine) {
                        syncQueue().catch(err => console.error("Background sync failed", err));
                    }

                    return { success: true, queued: true };
                }
            } catch (err) {
                console.error("Error coalescing updates:", err);
            }
        }

        // 2. Always add to queue first (Persistence & Instant UI response)
        await addToQueue(operationWithUser);
        await updatePendingCount();

        // 3. Trigger background sync if online (Fire and forget)
        if (navigator.onLine) {
            syncQueue().catch(err => console.error("Background sync failed triggered by op", err));
        }

        return { success: true, queued: true };
    }, [user, updatePendingCount, syncQueue]);

    // Cleanup corrupted queue items (sanitize data)
    const cleanupCorruptedQueue = useCallback(async () => {
        if (!user) return;

        try {
            const queue = await getQueue(user.uid);

            for (const item of queue) {
                // Sanitize the data in each queue item
                if (item.data) {
                    const sanitizedData = sanitizeForFirestore(item.data);
                    await updateQueueItem(item.id, { data: sanitizedData });
                }
            }


        } catch (error) {
            console.error('Error cleaning up queue:', error);
        }
    }, [user]);

    // Force sync (manual trigger)
    const forceSync = useCallback(async () => {
        if (!navigator.onLine) {
            setSyncError('No hay conexión a internet');
            return;
        }
        await syncQueue();
    }, [syncQueue]);

    // Check pending count on mount and when online status changes
    useEffect(() => {
        // Cleanup corrupted queue items on mount
        cleanupCorruptedQueue();
        updatePendingCount();

        const handleOnline = () => {
            updatePendingCount();
            // Auto-sync when coming online
            syncQueue();
        };

        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [updatePendingCount, syncQueue, cleanupCorruptedQueue]);

    const value = {
        queueOperation,
        pendingCount,
        isSyncing,
        lastSyncTime,
        syncError,
        forceSync,
        updatePendingCount
    };

    return (
        <SyncQueueContext.Provider value={value}>
            {children}
        </SyncQueueContext.Provider>
    );
};

export default SyncQueueProvider;
